import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useMatch, useNavigate } from 'react-router-dom';
import { useRedditAuth } from '../RedditAuthContext';
import redditApi from '../../../services/redditApi';
import OverviewTab from './OverviewTab';
import RedditorPerformanceTab from './RedditorPerformanceTab';
import OverdueReportTab from './OverdueReportTab';
import SubredditStatsTab from './SubredditStatsTab';
import ManageRedditorsTab from './ManageRedditorsTab';
import PaymentsTab from './PaymentsTab';
import ClientAssignmentsTab from './ClientAssignmentsTab';
import MonthlyClientReportTab from './MonthlyClientReportTab';
import RedditorDetailPage from './RedditorDetailPage';
import AccountPerformanceTab from './AccountPerformanceTab';
import UserAgentAnalysisTab from './UserAgentAnalysisTab';
import AccountQualityTab from './AccountQualityTab';
import { clearbitLogoFromWebsite } from '../../../utils/clearbitLogo';
import '../reddit.css';

/* ─── Tab definitions ────────────────────────────────────────────────────── */

const SECTIONS = [
  {
    label: 'Client Reports',
    tabs: [
      {
        id: 'overview', label: 'Overview',
        icon: (
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M7 14l3-3 3 2 4-5" />
          </svg>
        ),
      },
      {
        id: 'subreddits', label: 'Subreddit Stats',
        icon: (
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <rect x="3" y="3" width="18" height="18" rx="5" />
            <path d="M10 7v10 M14 7v10 M7 10h10 M7 14h10" />
          </svg>
        ),
      },
    ],
  },
  {
    label: 'Redditors',
    tabs: [
      {
        id: 'redditors', label: 'Redditor Performance',
        icon: (
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <circle cx="8" cy="7" r="3.5" />
            <path d="M2.5 19a5.5 5.5 0 0 1 11 0" />
            <path d="M20 11V6 M17 9l3-3 3 3" />
          </svg>
        ),
      },
      {
        id: 'account-performance', label: 'Account Performance',
        icon: (
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M4 4v16h16" />
            <path d="M7 14l4-4 3 2 5-6" />
          </svg>
        ),
      },
      {
        id: 'overdue', label: 'Overdue Report',
        icon: (
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <circle cx="12" cy="13" r="7" />
            <path d="M9 3h6 M12 3v3" />
            <path d="M12 10v3 M12 16h.01" />
          </svg>
        ),
      },
      {
        id: 'ua-analysis', label: 'User Agent Analysis',
        icon: (
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <rect x="3" y="4" width="18" height="14" rx="2" />
            <path d="M3 8h18" />
            <circle cx="10" cy="14" r="2.2" />
            <path d="M11.6 15.6l2.4 2.4" />
          </svg>
        ),
      },
      {
        id: 'account-quality', label: 'Account Quality Check',
        icon: (
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <rect x="5" y="4" width="14" height="18" rx="2" />
            <rect x="9" y="2" width="6" height="4" rx="1" />
            <path d="M9 13l2 2 4-4" />
          </svg>
        ),
      },
    ],
  },
  {
    label: 'Settings',
    tabs: [
      {
        id: 'manage', label: 'Manage Members',
        icon: (
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <circle cx="12" cy="8" r="3.5" />
            <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
          </svg>
        ),
      },
      {
        id: 'clients', label: 'Client Assignments',
        icon: (
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <rect x="3" y="7" width="18" height="13" rx="2" />
            <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            <path d="M3 12h18" />
          </svg>
        ),
      },
      {
        id: 'payments', label: 'Payments & Invoices',
        icon: (
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M7 3h10v18l-2-1.3-3 1.3-3-1.3-2 1.3z" />
            <path d="M10 8h4 M10 12h4 M10 16h2" />
          </svg>
        ),
      },
      {
        id: 'monthly-client-report', label: 'Monthly Client Report',
        icon: (
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M4 19V5" />
            <path d="M4 19h16" />
            <path d="M8 15v-4" />
            <path d="M12 15V7" />
            <path d="M16 15v-6" />
          </svg>
        ),
      },
    ],
  },
];

const ACCOUNTANT_TAB_IDS = new Set(['overview', 'payments']);

/* ─── Client Selector ────────────────────────────────────────────────────── */

/* Build a multi-line hover tooltip showing the per-category "needs review"
   breakdown for a client (used on the client name/logo in the sidebar). */
function reviewBreakdownTitle(c) {
  if (!c) return '';
  const parts = [];
  if (c.skipped_count) parts.push(`Skipped: ${c.skipped_count}`);
  if (c.overdue_count) parts.push(`Overdue: ${c.overdue_count}`);
  if (c.replacement_required_count) parts.push(`Replacement required: ${c.replacement_required_count}`);
  if (c.archived_review_count) parts.push(`Archived: ${c.archived_review_count}`);
  const total = c.needs_review_count || 0;
  if (!parts.length) return `${c.client_name} — no comments need review`;
  return `${c.client_name}\nNeeds review: ${total}\n${parts.join('\n')}`;
}

function ClientLogo({ client, size = 20 }) {
  const [err, setErr] = useState(false);
  const src = client?.logo_url || clearbitLogoFromWebsite(client?.website);
  useEffect(() => { setErr(false); }, [src]);
  const initials = (client?.client_name || '?').trim().charAt(0).toUpperCase();
  if (!src || err) {
    return (
      <span style={{
        width: size, height: size, borderRadius: 6, flexShrink: 0,
        background: '#f1f5f9', color: '#475569', fontSize: size * 0.5, fontWeight: 700,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>{initials}</span>
    );
  }
  return (
    <img
      src={src}
      alt=""
      onError={() => setErr(true)}
      style={{ width: size, height: size, borderRadius: 6, flexShrink: 0, objectFit: 'contain', background: '#fff' }}
    />
  );
}

function ClientSelector({ selectedClient, onSelect, collapsed, clients }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const panelWidth = Math.min(320, typeof window !== 'undefined' ? window.innerWidth - 24 : 320);

  useEffect(() => {
    function handleClick(e) {
      if (!open) return;
      const t = e.target;
      if (ref.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Position the panel with `position: fixed` so it escapes the sidebar's
  // `overflow: hidden` and can float over the main content without disturbing
  // the sidebar width.
  const computePos = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const left = Math.min(r.left, window.innerWidth - panelWidth - 8);
    setPanelPos({ top: r.bottom + 4, left: Math.max(8, left) });
  }, [panelWidth]);

  useEffect(() => {
    if (!open) return;
    computePos();
    const onResize = () => computePos();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, computePos]);

  const filtered = clients
    .filter((c) => c.client_name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.client_name.localeCompare(b.client_name, undefined, { sensitivity: 'base' }));

  const selectedObj = clients.find((c) => c.client_name === selectedClient) || null;
  const label = selectedClient || '—';

  const panel = open && createPortal(
    <div ref={panelRef} style={{
      position: 'fixed', top: panelPos.top, left: panelPos.left, width: panelWidth,
      background: '#fff', border: '1px solid var(--rh-border)', borderRadius: 10,
      boxShadow: 'var(--rh-shadow-md)', zIndex: 99999, overflow: 'hidden',
    }}>
      <DropdownBody
        search={search} setSearch={setSearch} filtered={filtered}
        selectedClient={selectedClient} onSelect={(n) => { onSelect(n); setOpen(false); setSearch(''); }}
      />
    </div>,
    document.body
  );

  if (collapsed) {
    return (
      <div ref={ref} style={{ position: 'relative', display: 'flex', justifyContent: 'center', padding: '8px 0 10px' }}>
        <button
          ref={triggerRef}
          onClick={() => setOpen((v) => !v)}
          title={selectedObj ? reviewBreakdownTitle(selectedObj) : 'Open client selector'}
          style={{
            width: 40, height: 40, borderRadius: 10, border: '1.5px solid var(--rh-border)',
            background: '#fff', cursor: 'pointer', padding: 0, display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', position: 'relative',
            ...(open ? { borderColor: 'var(--rh-orange)' } : {}),
          }}
        >
          {selectedObj ? <ClientLogo client={selectedObj} size={26} /> : (
            <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--rh-orange)' }}>R</span>
          )}
          {(selectedObj?.needs_review_count || 0) > 0 && (
            <span className="rh-review-badge" style={{ position: 'absolute', top: -6, right: -6 }}>
              {selectedObj.needs_review_count}
            </span>
          )}
        </button>
        {panel}
      </div>
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative', padding: '0 12px 12px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--rh-text-faint)', marginBottom: 6, paddingLeft: 4 }}>
        Client
      </div>
      <button
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        title={selectedObj ? reviewBreakdownTitle(selectedObj) : 'Open client selector'}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 8, background: '#fff', border: '1.5px solid var(--rh-border)', borderRadius: 8,
          padding: '8px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
          color: 'var(--rh-orange)',
          transition: 'border-color 0.15s',
          ...(open ? { borderColor: 'var(--rh-orange)' } : {}),
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
          {selectedObj && <ClientLogo client={selectedObj} size={22} />}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
          {(selectedObj?.needs_review_count || 0) > 0 && (
            <span className="rh-review-badge" title={reviewBreakdownTitle(selectedObj)}>
              {selectedObj.needs_review_count}
            </span>
          )}
        </span>
        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"
          style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {panel}
    </div>
  );
}

/* Shared dropdown body (search + list) used by both expanded and collapsed renders. */
function DropdownBody({ search, setSearch, filtered, selectedClient, onSelect }) {
  return (
    <>
      <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--rh-border)' }}>
        <input
          autoFocus
          placeholder="Search clients…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: '100%', border: '1px solid var(--rh-border)', borderRadius: 6,
            padding: '6px 10px', fontSize: 12, outline: 'none', boxSizing: 'border-box',
          }}
        />
      </div>
      <div style={{ maxHeight: 260, overflowY: 'auto' }}>
        {filtered.map((c) => (
          <button
            key={c.client_name}
            onClick={() => onSelect(c.client_name)}
            title={reviewBreakdownTitle(c)}
            style={{
              width: '100%', textAlign: 'left',
              background: selectedClient === c.client_name ? 'var(--rh-orange-light)' : 'transparent',
              border: 'none', padding: '9px 12px', fontSize: 13,
              fontWeight: selectedClient === c.client_name ? 600 : 400,
              color: selectedClient === c.client_name ? 'var(--rh-orange)' : 'var(--rh-text)',
              cursor: 'pointer', borderTop: '1px solid var(--rh-border)',
              display: 'flex', alignItems: 'center', gap: 10,
            }}
          >
            <ClientLogo client={c} size={20} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
              {c.client_name}
            </span>
            {(c.needs_review_count || 0) > 0 && (
              <span className="rh-review-badge" title={reviewBreakdownTitle(c)}>
                {c.needs_review_count}
              </span>
            )}
          </button>
        ))}
        {filtered.length === 0 && (
          <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--rh-text-faint)' }}>No clients found.</div>
        )}
      </div>
    </>
  );
}

/* ─── Main Dashboard ─────────────────────────────────────────────────────── */

export default function AdminDashboard() {
  const { user, logout } = useRedditAuth();
  const isAccountant = user?.is_accountant && !user?.is_admin;
  const redditorDetailMatch = useMatch('/reddit/admin/redditors/:redditorId');
  const navigate = useNavigate();

  const allTabs = SECTIONS.flatMap((s) => s.tabs);
  const visibleTabIds = new Set(
    isAccountant ? allTabs.filter((t) => ACCOUNTANT_TAB_IDS.has(t.id)).map((t) => t.id) : allTabs.map((t) => t.id)
  );
  const visibleSections = SECTIONS
    .map((s) => ({ ...s, tabs: s.tabs.filter((t) => visibleTabIds.has(t.id)) }))
    .filter((s) => s.tabs.length > 0);

  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = isAccountant ? 'payments' : 'overview';
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    visibleTabIds.has(tabFromUrl) ? tabFromUrl : defaultTab
  );
  // Sidebar starts collapsed by default, but remembers the user's manual choice.
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('reddit_sidebar_collapsed');
    return saved === null ? true : saved === 'true';
  });
  const clientFromUrl = searchParams.get('client') || null;
  const [selectedClient, setSelectedClient] = useState(clientFromUrl);
  const [clientList, setClientList] = useState([]);
  // Incremented whenever a dev action completes so all tabs refresh their data.
  const [devActionVersion, setDevActionVersion] = useState(0);

  // Instant collapsed-sidebar tooltip (native title attr has a ~1s delay).
  const [tip, setTip] = useState(null); // { text, top, left } | null

  function showTip(e, text) {
    if (!collapsed || !text) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTip({ text, top: rect.top + rect.height / 2, left: rect.right + 10 });
  }
  function hideTip() { setTip(null); }

  // Sync selectedClient → URL whenever it changes
  function handleSelectClient(name) {
    setSelectedClient(name);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (name) next.set('client', name);
      else next.delete('client');
      return next;
    }, { replace: true });
  }

  // Fetch client list once; auto-select first client only if URL had none
  useEffect(() => {
    if (isAccountant) return;
    redditApi.get('/admin/clients/').then(({ data }) => {
      const clients = data.clients || [];
      setClientList(clients);
      if (clients.length > 0 && !selectedClient) {
        handleSelectClient(clients[0].client_name);
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAccountant]);

  // Refresh the client list (active-only by default). If the currently selected
  // client was just archived, clear/reselect so Overview doesn't keep a hidden client.
  function refreshClientList(opts = {}) {
    const archivedName = opts.archivedClientName || null;
    redditApi.get('/admin/clients/').then(({ data }) => {
      const clients = data.clients || [];
      setClientList(clients);
      if (archivedName && selectedClient === archivedName) {
        handleSelectClient(clients[0]?.client_name || '');
      }
    }).catch(() => {});
  }

  function handleTabChange(id) {
    setActiveTab(id);
    setMobileNavOpen(false);
    if (redditorDetailMatch) {
      navigate(`/reddit?tab=${id}`, { replace: true });
    } else {
      setSearchParams({ tab: id }, { replace: true });
    }
  }

  const [stats, setStats] = useState(null);
  const [globalStats, setGlobalStats] = useState(null);
  const [selfAddReviewCount, setSelfAddReviewCount] = useState(0);
  const navRef = useRef(null);
  const btnRefs = useRef({});
  const [indicatorStyle, setIndicatorStyle] = useState({ top: 0, height: 0, opacity: 0 });
  const [loadingStats, setLoadingStats] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const POLL_INTERVAL_MS = 15_000;

  const fetchStats = useCallback(async () => {
    try {
      const params = selectedClient ? `?client=${encodeURIComponent(selectedClient)}` : '';
      const { data } = await redditApi.get(`/admin/stats/${params}`);
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats', err);
    } finally {
      setLoadingStats(false);
    }
  }, [selectedClient]);

  const fetchGlobalStats = useCallback(async () => {
    try {
      const { data } = await redditApi.get('/admin/stats/');
      setGlobalStats(data);
    } catch (err) {
      console.error('Failed to load global stats', err);
    }
  }, []);

  const fetchSelfAddReviewCount = useCallback(async (maybeCount) => {
    if (typeof maybeCount === 'number') {
      setSelfAddReviewCount(maybeCount);
      return;
    }
    try {
      const { data } = await redditApi.get('/admin/accounts/self-added-review/');
      setSelfAddReviewCount(data.unreviewed_self_added || 0);
    } catch {
      // Non-critical badge
    }
  }, []);

  useEffect(() => {
    setLoadingStats(true);
    fetchStats();
    const id = setInterval(fetchStats, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchStats]);

  useEffect(() => {
    fetchGlobalStats();
    const id = setInterval(fetchGlobalStats, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchGlobalStats]);

  useEffect(() => {
    if (isAccountant) return undefined;
    fetchSelfAddReviewCount();
    const id = setInterval(fetchSelfAddReviewCount, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchSelfAddReviewCount, isAccountant]);

  const measureIndicator = useCallback(() => {
    const btn = btnRefs.current[activeTab];
    const nav = navRef.current;
    if (!btn || !nav) return;
    const navTop = nav.getBoundingClientRect().top;
    const btnRect = btn.getBoundingClientRect();
    setIndicatorStyle({
      top: btnRect.top - navTop + nav.scrollTop,
      height: btnRect.height,
      opacity: 1,
    });
  }, [activeTab]);

  // Re-measure on active-tab / collapse changes.
  useEffect(() => {
    measureIndicator();
  }, [measureIndicator, collapsed]);

  // Re-measure while the sidebar width transition runs (collapse ↔ expand) and
  // on any nav resize. Without this, the indicator lands mid-transition and
  // sits in the wrong spot until a later re-render.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measureIndicator());
    ro.observe(nav);
    const onTransitionEnd = (e) => {
      if (e.propertyName === 'width') measureIndicator();
    };
    const sidebar = nav.closest('.rh-sidebar');
    sidebar?.addEventListener('transitionend', onTransitionEnd);
    return () => {
      ro.disconnect();
      sidebar?.removeEventListener('transitionend', onTransitionEnd);
    };
  }, [measureIndicator]);

  // Persist the collapse choice so it survives reloads/logins.
  useEffect(() => {
    localStorage.setItem('reddit_sidebar_collapsed', String(collapsed));
  }, [collapsed]);

  const initials = (user?.full_name || '?').charAt(0).toUpperCase();

  const activeTabLabel = visibleSections.flatMap((s) => s.tabs).find((t) => t.id === activeTab)?.label || 'Dashboard';

  return (
    <div className="rh-app" style={{ flexDirection: 'column' }}>

      {/* Mobile header bar — visible only on ≤640px */}
      <div className="rh-mobile-header">
        <div className="rh-mobile-header-brand">
          <div className="rh-brand-icon" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8, flexShrink: 0 }}>
            <svg viewBox="0 0 20 20" width="18" height="18" fill="#fff" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.67 10a1.46 1.46 0 0 0-2.47-1 7.12 7.12 0 0 0-3.85-1.23l.65-3.08 2.13.45a1 1 0 1 0 .14-.68l-2.38-.5a.27.27 0 0 0-.32.2l-.73 3.44a7.14 7.14 0 0 0-3.83 1.23 1.46 1.46 0 1 0-1.61 2.39 2.87 2.87 0 0 0 0 .44c0 2.24 2.61 4.06 5.83 4.06s5.83-1.82 5.83-4.06a2.87 2.87 0 0 0 0-.44A1.46 1.46 0 0 0 16.67 10zM8.08 11a1 1 0 1 1 1 1 1 1 0 0 1-1-1zm5.46 2.71a3.45 3.45 0 0 1-4.24 0 .27.27 0 1 1 .33-.42 2.93 2.93 0 0 0 3.58 0 .27.27 0 1 1 .33.42zm-.18-1.71a1 1 0 1 1 1-1 1 1 0 0 1-1 1z"/>
            </svg>
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="rh-mobile-header-title">Reddit Hub</div>
            {selectedClient && <div className="rh-mobile-header-client">{selectedClient}</div>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 12, color: 'var(--rh-text-muted)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {activeTabLabel}
          </span>
          <button className="rh-hamburger" onClick={() => setMobileNavOpen(true)} aria-label="Open navigation">
            <span /><span /><span />
          </button>
        </div>
      </div>

      {/* Mobile nav overlay + drawer */}
      {mobileNavOpen && (
        <>
          <div className="rh-mobile-drawer-overlay" onClick={() => setMobileNavOpen(false)} />
          <div className="rh-mobile-drawer">
            {/* Drawer header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid var(--rh-border)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="rh-brand-icon" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8 }}>
                  <svg viewBox="0 0 20 20" width="18" height="18" fill="#fff" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16.67 10a1.46 1.46 0 0 0-2.47-1 7.12 7.12 0 0 0-3.85-1.23l.65-3.08 2.13.45a1 1 0 1 0 .14-.68l-2.38-.5a.27.27 0 0 0-.32.2l-.73 3.44a7.14 7.14 0 0 0-3.83 1.23 1.46 1.46 0 1 0-1.61 2.39 2.87 2.87 0 0 0 0 .44c0 2.24 2.61 4.06 5.83 4.06s5.83-1.82 5.83-4.06a2.87 2.87 0 0 0 0-.44A1.46 1.46 0 0 0 16.67 10zM8.08 11a1 1 0 1 1 1 1 1 1 0 0 1-1-1zm5.46 2.71a3.45 3.45 0 0 1-4.24 0 .27.27 0 1 1 .33-.42 2.93 2.93 0 0 0 3.58 0 .27.27 0 1 1 .33.42zm-.18-1.71a1 1 0 1 1 1-1 1 1 0 0 1-1 1z"/>
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--rh-text)' }}>Reddit Hub</div>
                  <div style={{ fontSize: 10, color: 'var(--rh-text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {isAccountant ? 'Accountant' : 'Admin'}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setMobileNavOpen(false)}
                aria-label="Close navigation"
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--rh-text-muted)', padding: '4px 6px', borderRadius: 6 }}
              >×</button>
            </div>

            {/* Client selector in drawer */}
            {!isAccountant && (
              <ClientSelector
                selectedClient={selectedClient}
                onSelect={(name) => { handleSelectClient(name); setMobileNavOpen(false); }}
                collapsed={false}
                clients={clientList}
              />
            )}

            {/* Nav items */}
            <nav style={{ flex: 1, overflowY: 'auto', paddingTop: 8 }}>
              {visibleSections.map((section, si) => (
                <div key={section.label}>
                  <div style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                    color: 'var(--rh-text-faint)', padding: si === 0 ? '4px 16px 6px' : '16px 16px 6px',
                  }}>
                    {section.label}
                  </div>
                  {section.tabs.map((t) => (
                    <button
                      key={t.id}
                      className={`rh-nav-btn${activeTab === t.id ? ' active' : ''}`}
                      onClick={() => handleTabChange(t.id)}
                      style={{ width: '100%', justifyContent: 'flex-start', padding: '0 16px 0 12px', borderRadius: 0, borderLeft: activeTab === t.id ? '3px solid var(--rh-orange)' : '3px solid transparent' }}
                    >
                      <span className="rh-nav-icon">{t.icon}</span>
                      <span className="rh-nav-label">{t.label}</span>
                      {t.id === 'account-performance' && selfAddReviewCount > 0 && (
                        <span className="rh-review-badge" style={{ marginLeft: 'auto' }}>
                          {selfAddReviewCount > 99 ? '99+' : selfAddReviewCount}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              ))}
            </nav>

            {/* Footer — sign out */}
            <div style={{ borderTop: '1px solid var(--rh-border)', padding: '12px 16px', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div className="rh-user-avatar">{initials}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--rh-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.full_name}</div>
                  <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</div>
                </div>
              </div>
              <button
                className="rh-signout-btn"
                onClick={() => { setMobileNavOpen(false); logout(); }}
                style={{ width: '100%', justifyContent: 'flex-start' }}
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span>Sign out</span>
              </button>
            </div>
          </div>
        </>
      )}

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
      <div className={`rh-sidebar-wrapper${collapsed ? ' rh-sidebar-wrapper--collapsed' : ''}`}>
        <aside className={`rh-sidebar${collapsed ? ' rh-sidebar--collapsed' : ''}`}>

          {/* Brand header */}
          <div className="rh-sidebar-header">
            <div className="rh-brand-icon">
              <svg viewBox="0 0 20 20" width="26" height="26" fill="#fff" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.67 10a1.46 1.46 0 0 0-2.47-1 7.12 7.12 0 0 0-3.85-1.23l.65-3.08 2.13.45a1 1 0 1 0 .14-.68l-2.38-.5a.27.27 0 0 0-.32.2l-.73 3.44a7.14 7.14 0 0 0-3.83 1.23 1.46 1.46 0 1 0-1.61 2.39 2.87 2.87 0 0 0 0 .44c0 2.24 2.61 4.06 5.83 4.06s5.83-1.82 5.83-4.06a2.87 2.87 0 0 0 0-.44A1.46 1.46 0 0 0 16.67 10zM8.08 11a1 1 0 1 1 1 1 1 1 0 0 1-1-1zm5.46 2.71a3.45 3.45 0 0 1-4.24 0 .27.27 0 1 1 .33-.42 2.93 2.93 0 0 0 3.58 0 .27.27 0 1 1 .33.42zm-.18-1.71a1 1 0 1 1 1-1 1 1 0 0 1-1 1z"/>
              </svg>
            </div>
            {!collapsed && (
              <>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="rh-sidebar-brand-text">Reddit Hub</div>
                  <div className="rh-sidebar-role">{isAccountant ? 'Accountant' : 'Admin'}</div>
                </div>
                <button
                  className="rh-collapse-btn"
                  onClick={() => setCollapsed(true)}
                  aria-label="Collapse sidebar"
                  title="Collapse"
                >
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
              </>
            )}
          </div>

          {/* Client selector (admins only) */}
          {!isAccountant && (
            <ClientSelector
              selectedClient={selectedClient}
              onSelect={handleSelectClient}
              collapsed={collapsed}
              clients={clientList}
            />
          )}

          {/* Sectioned nav */}
          <nav className="rh-sidebar-nav" ref={navRef} style={{ position: 'relative', paddingTop: collapsed ? 16 : 4 }}>
            <div
              className="rh-nav-indicator"
              style={{ top: indicatorStyle.top, height: indicatorStyle.height, opacity: indicatorStyle.opacity }}
            />
            {visibleSections.map((section, si) => (
              <div key={section.label}>
                {!collapsed && (
                  <div style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                    color: 'var(--rh-text-faint)', padding: si === 0 ? '4px 16px 6px' : '16px 16px 6px',
                  }}>
                    {section.label}
                  </div>
                )}
                {collapsed && si > 0 && (
                  <div style={{ height: 1, background: 'var(--rh-border)', margin: '8px 12px' }} />
                )}
                {section.tabs.map((t) => (
                  <button
                    key={t.id}
                    ref={(el) => { btnRefs.current[t.id] = el; }}
                    className={`rh-nav-btn${activeTab === t.id ? ' active' : ''}`}
                    onClick={() => handleTabChange(t.id)}
                    onMouseEnter={(e) => showTip(e, t.label)}
                    onMouseLeave={hideTip}
                    onFocus={(e) => showTip(e, t.label)}
                    onBlur={hideTip}
                  >
                    <span className="rh-nav-icon" style={{ position: 'relative' }}>
                      {t.icon}
                      {t.id === 'account-performance' && selfAddReviewCount > 0 && (
                        <span
                          className="rh-review-badge"
                          style={{ position: 'absolute', top: -6, right: -8 }}
                          title={`${selfAddReviewCount} self-added account${selfAddReviewCount === 1 ? '' : 's'} need niche review`}
                        >
                          {selfAddReviewCount > 99 ? '99+' : selfAddReviewCount}
                        </span>
                      )}
                    </span>
                    {!collapsed && <span className="rh-nav-label">{t.label}</span>}
                    {!collapsed && t.id === 'account-performance' && selfAddReviewCount > 0 && (
                      <span className="rh-review-badge" style={{ marginLeft: 'auto' }}>
                        {selfAddReviewCount > 99 ? '99+' : selfAddReviewCount}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </nav>

          {/* Footer */}
          <div className="rh-sidebar-footer">
            <button
              className={`rh-signout-btn${collapsed ? ' rh-signout-btn--collapsed' : ''}`}
              onClick={logout}
              onMouseEnter={(e) => showTip(e, 'Sign out')}
              onMouseLeave={hideTip}
              onFocus={(e) => showTip(e, 'Sign out')}
              onBlur={hideTip}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              {!collapsed && <span>Sign out</span>}
            </button>
            <div className={`rh-sidebar-user${collapsed ? ' rh-sidebar-user--collapsed' : ''}`} style={{ marginBottom: 0, marginTop: 8 }}>
              <div
                className="rh-user-avatar"
                onMouseEnter={(e) => showTip(e, user?.full_name)}
                onMouseLeave={hideTip}
                onFocus={(e) => showTip(e, user?.full_name)}
                onBlur={hideTip}
                tabIndex={0}
              >{initials}</div>
              {!collapsed && (
                <div style={{ minWidth: 0 }}>
                  <div className="rh-user-name">{user?.full_name}</div>
                  <div className="rh-user-email">{user?.email}</div>
                </div>
              )}
            </div>
          </div>
        </aside>

        {collapsed && (
          <button
            className="rh-expand-peek-btn"
            onClick={() => setCollapsed(false)}
            aria-label="Expand sidebar"
            onMouseEnter={(e) => showTip(e, 'Expand')}
            onMouseLeave={hideTip}
            onFocus={(e) => showTip(e, 'Expand')}
            onBlur={hideTip}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        )}
      </div>

      {/* Main content */}
      <main className="rh-main">
        {redditorDetailMatch ? (
          <div className="rh-tab-content">
            <RedditorDetailPage />
          </div>
        ) : (
          <div key={`${activeTab}-${selectedClient}`} className="rh-tab-content">
            {activeTab === 'overview' && (
              <OverviewTab
                stats={stats}
                loading={loadingStats}
                onRefresh={() => { fetchStats(); refreshClientList(); setDevActionVersion((v) => v + 1); }}
                user={user}
                selectedClient={selectedClient}
                assignedRedditor={(clientList.find((c) => c.client_name === selectedClient)?.assigned_redditors || [])
                  .map((r) => r.name)
                  .filter(Boolean)
                  .join(', ') || clientList.find((c) => c.client_name === selectedClient)?.assigned_redditor_name || null}
                assignedRedditorId={(clientList.find((c) => c.client_name === selectedClient)?.assigned_redditors || [])[0]?.id
                  || clientList.find((c) => c.client_name === selectedClient)?.assigned_redditor_id || null}
              />
            )}
            {activeTab === 'redditors' && (
              <RedditorPerformanceTab stats={globalStats} loading={!globalStats} />
            )}
            {activeTab === 'overdue' && (
              <OverdueReportTab />
            )}
            {activeTab === 'ua-analysis' && (
              <UserAgentAnalysisTab />
            )}
            {activeTab === 'account-quality' && (
              <AccountQualityTab />
            )}
            {activeTab === 'subreddits' && (
              <SubredditStatsTab stats={stats} loading={loadingStats} selectedClient={selectedClient} />
            )}
            {activeTab === 'manage' && (
              <ManageRedditorsTab onRedditorChange={fetchStats} />
            )}
            {activeTab === 'payments' && (
              <PaymentsTab />
            )}
            {activeTab === 'clients' && (
              <ClientAssignmentsTab
                refreshTrigger={devActionVersion}
                onClientsChanged={refreshClientList}
              />
            )}
            {activeTab === 'monthly-client-report' && (
              <MonthlyClientReportTab />
            )}
            {activeTab === 'account-performance' && (
              <AccountPerformanceTab onSelfAddReviewCountChange={fetchSelfAddReviewCount} />
            )}
          </div>
        )}
      </main>
      </div>

      {/* Instant collapsed-sidebar tooltip (rendered outside the sidebar so it
          isn't clipped by overflow:hidden). Appears immediately on hover/focus. */}
      {tip && createPortal(
        <div
          role="tooltip"
          style={{
            position: 'fixed',
            top: tip.top,
            left: tip.left,
            transform: 'translateY(-50%)',
            zIndex: 99999,
            background: '#1f2937',
            color: '#fff',
            fontSize: '0.78rem',
            fontWeight: 600,
            padding: '5px 10px',
            borderRadius: 6,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}
        >
          {tip.text}
        </div>,
        document.body,
      )}
    </div>
  );
}
